// frontend/src/components/Modal/CreateEventModalityModal.js
import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import styled from 'styled-components';
import Button from '../Button/Button';
import Typography from '../Typography';
import CardToogle from '../Toogle/CardToogle';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import { getVehicleTypeSvgIcon } from '../../utilities';
// import ProfileIconOutline from '../Icons/ProfileIconOutline';
import { VEHICLE_TYPES } from '../../constants/vehicleTypes';
import NoVehiclesIcon from '../Icons/Vehicles/NoVehiclesIcon';

const CreateEventModalityModal = ({
  needsVehicle,
  allowedVehicleTypes,
  onSave,
  onClose
}) => {
  const { t } = useTranslation(['createEvent', 'vehicleTypes']);

  const vehicleTypes = useMemo(() => ([
    VEHICLE_TYPES.MOTORCYCLE,
    VEHICLE_TYPES.CAR,
    VEHICLE_TYPES.ATV,
    VEHICLE_TYPES.BUGGY,
    VEHICLE_TYPES.GO_KART,
  ]), []);

  const validVehicleTypeSet = useMemo(() => new Set(vehicleTypes), [vehicleTypes]);

  const initialSelection = useMemo(() => {
    if (needsVehicle === false) {
      return { needsVehicle: false, allowedVehicleTypes: [] };
    }

    const incoming = Array.isArray(allowedVehicleTypes) ? allowedVehicleTypes : [];
    const filtered = incoming.filter((type) => validVehicleTypeSet.has(type));
    const ensured = filtered.length > 0 ? filtered : [VEHICLE_TYPES.MOTORCYCLE];
    return { needsVehicle: true, allowedVehicleTypes: ensured };
  }, [allowedVehicleTypes, needsVehicle, validVehicleTypeSet]);

  const [needsVehicleLocal, setNeedsVehicleLocal] = useState(initialSelection.needsVehicle);
  const [allowedVehicleTypesLocal, setAllowedVehicleTypesLocal] = useState(initialSelection.allowedVehicleTypes);

  const handleToggleNoVehicle = () => {
    setNeedsVehicleLocal(false);
    setAllowedVehicleTypesLocal([]);
  };

  const handleToggleVehicleType = (vehicleType) => {
    setNeedsVehicleLocal(true);
    setAllowedVehicleTypesLocal((prev) => {
      const prevFiltered = Array.isArray(prev) ? prev.filter((t) => validVehicleTypeSet.has(t)) : [];
      const isSelected = prevFiltered.includes(vehicleType);

      if (isSelected) {
        const next = prevFiltered.filter((t) => t !== vehicleType);
        return next.length === 0 ? prevFiltered : next;
      }

      return [...prevFiltered, vehicleType];
    });
  };

  return (
    <Modal onClose={onClose} title={t('createEventModalityModal.title')} closeOnOverlayClick={false}>
      <ModalContent>
        <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak}>
          {t('createEventModalityModal.description')}
        </Typography>
        <VehicleTypesSection>
          {/* <Typography as="p" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('createEventModalityModal.allowedVehicleTypes')}
          </Typography>
          <Typography as="p" $variant="body-3-regular" color={theme.colors.defaultWeak}>
            {t('createEventModalityModal.allowedVehicleTypesDescription')}
          </Typography> */}
          <VehicleTypeGrid>
            {vehicleTypes.map((vehicleType) => {
              const isActive = needsVehicleLocal && allowedVehicleTypesLocal.includes(vehicleType);
              return (
                <CardToogle
                  key={vehicleType}
                  category={t(`allowedVehicleTypes.${vehicleType}`, { ns: 'vehicleTypes' })}
                  isActive={isActive}
                  onClick={() => handleToggleVehicleType(vehicleType)}
                  icon={getVehicleTypeSvgIcon(vehicleType, isActive ? theme.colors.brandMain : theme.colors.defaultSubtle)}
                  iconSize="48px"
                />
              );
            })}
            <CardToogle
              key="no-vehicle"
              category={t('createEventModalityModal.withoutVehicle')}
              isActive={!needsVehicleLocal}
              onClick={handleToggleNoVehicle}
              icon={<NoVehiclesIcon stroke={!needsVehicleLocal ? theme.colors.brandMain : theme.colors.defaultSubtle} />}
              iconSize="48px"
            />
          </VehicleTypeGrid>
        </VehicleTypesSection>
      </ModalContent>
      <FormActions>
        <Button
          size="medium"
          $fullWidth
          onClick={() => {
            const payload = needsVehicleLocal ? allowedVehicleTypesLocal : [];
            onSave?.({ needsVehicle: needsVehicleLocal, allowedVehicleTypes: payload });
            onClose?.();
          }}
        >
          {t('createEventModalityModal.save')}
        </Button>
      </FormActions>
    </Modal>
  );
};

export default CreateEventModalityModal;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 100%;
  }
`;

const FormActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm};
  text-align: center;
  width: 100%;
`;

const VehicleTypesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const VehicleTypeGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
